import { FC, useEffect, useRef, useState } from "react";
import { Box, CircularProgress } from "@mui/material";

export interface VideoPlayerProps {
  videoSrc: string | null;
  thumbnail: string | null;
  isExpanded: boolean;
  onVideoLoaded?: (duration: number) => void;
  fileKey?: string;
}

export const VideoPlayer: FC<VideoPlayerProps> = ({
  videoSrc,
  isExpanded,
  onVideoLoaded,
  fileKey,
}) => {
  const [isLoading, setIsLoading] = useState(true);
  const videoRef = useRef<HTMLVideoElement>(null);

  useEffect(() => {
    if (videoSrc && videoRef.current) {
      const video = videoRef.current;

      const handleMetadata = () => {
        if (onVideoLoaded) {
          onVideoLoaded(video.duration);
        }
      };

      video.addEventListener("loadedmetadata", handleMetadata);

      return () => {
        video.removeEventListener("loadedmetadata", handleMetadata);
      };
    }
  }, [videoSrc, onVideoLoaded]);

  const handleLoadedData = () => {
    setIsLoading(false);
    if (videoRef.current && onVideoLoaded) {
      onVideoLoaded(videoRef.current.duration);
    }
  };

  if (!isExpanded) return null;

  return (
    <Box
      sx={{
        mt: 1,
        position: "relative",
        borderRadius: 1,
        overflow: "hidden",
        bgcolor: "#000",
      }}
    >
      {isLoading && (
        <Box
          sx={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            bgcolor: "background.paper",
            zIndex: 1,
          }}
        >
          <CircularProgress size={40} />
        </Box>
      )}

      <video
        ref={videoRef}
        src={videoSrc || undefined}
        style={{
          width: "100%",
          maxHeight: 300,
          objectFit: "contain",
          backgroundColor: "#000",
          display: "block",
        }}
        onLoadedData={handleLoadedData}
        controls
        playsInline
        key={fileKey}
      />
    </Box>
  );
};

export default VideoPlayer;
